import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Job, JobList } from '../interfaces/job-list';
import { JobListService } from '../services/job-list.service';
import { environment } from 'src/environments/environment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs';

const COMPANY_COLORS = [
  '#e8f0ff',
  '#fff0f5',
  '#f0f4ff',
  '#fff8ed',
  '#f0fdf4',
  '#fdf4ff',
  '#fef9f0',
  '#f5f5f5',
];

function getColor(index: number): string {
  return COMPANY_COLORS[index % COMPANY_COLORS.length];
}

type TagTheme = {
  background: string;
  border: string;
  accent: string;
  text: string;
};

const TAG_THEMES: TagTheme[] = [
  { background: '#eef4ff', border: '#c9d9ff', accent: '#3575e2', text: '#163b7a' },
  { background: '#fff1f6', border: '#f4c6d3', accent: '#d0385e', text: '#7c2740' },
  { background: '#f0fdf4', border: '#bfe8c8', accent: '#0f9b57', text: '#14532d' },
  { background: '#fff7ed', border: '#f6d1a6', accent: '#ea7a18', text: '#7a4312' },
  { background: '#f5f3ff', border: '#d9c9ff', accent: '#7c3aed', text: '#4c1d95' },
  { background: '#ecfeff', border: '#b9ecef', accent: '#0891b2', text: '#155e75' },
];

function hashString(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function timeAgo(dateStr: string): { label: string; daysAgo: number } {
  const created = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - created.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 60) return { label: `${diffMins} min ago`, daysAgo: 0 };
  if (diffHours < 24) return { label: `${diffHours}h ago`, daysAgo: 0 };
  if (diffDays === 1) return { label: '1 day ago', daysAgo: 1 };
  return { label: `${diffDays} days ago`, daysAgo: diffDays };
}

function mapApiJob(apiJob: JobList, index: number = 0): Job {
  const { label, daysAgo } = timeAgo(apiJob.createdAt);
  const isConfidential = !!apiJob.confidential;
  return {
    id: apiJob.id,
    title: apiJob.jobPostTitle ?? apiJob.position?.title ?? 'Unknown Position',
    jobPostTitle: apiJob.jobPostTitle,
    positionTitle: apiJob.positionTitle ?? apiJob.position?.title,
    company: isConfidential ? 'Confidential' : (apiJob.companyName ?? 'Unknown Company'),
    companyId: isConfidential ? '' : apiJob.companyId,
    companyLogoUrl: !isConfidential && apiJob.companyLogoUrl
      ? `${environment.apiImagePath}${apiJob.companyLogoUrl.replace(/\\/g, '/')}`
      : '',
    companyColor: getColor(index),
    companyInitial: isConfidential
      ? 'C'
      : (apiJob.companyName ?? 'C').charAt(0).toUpperCase(),
    type: apiJob.jobType ?? 'Full-time',
    salaryMin: apiJob.hideSalary ? null : apiJob.minSalary,
    salaryMax: apiJob.hideSalary ? null : apiJob.maxSalary,
    hideSalary: apiJob.hideSalary ?? false,
    location:
      apiJob.city && apiJob.country
        ? `${apiJob.city}, ${apiJob.country}`
        : (apiJob.city ?? apiJob.country ?? 'N/A'),
    workplace: apiJob.workplace ?? '',
    postedAgo: label,
    postedDaysAgo: daysAgo,
    minExperience: apiJob.minExperience ?? null,
    maxExperience: apiJob.maxExperience ?? null,
    description: apiJob.jobDescription ?? 'No description provided.',
    skills: apiJob.skillsAndTools ?? [],
    categories: (apiJob.categories ?? []).map((c) => c.name),
    requirements: apiJob.requirements ?? [],
    benefits: apiJob.benefits ?? [],
    responsibilities: apiJob.responsibilities ?? [],
    isNew: daysAgo === 0,
    jobLevel: apiJob.jobLevel ?? apiJob.position?.jobLevel ?? '',
    department: apiJob.position?.department ?? '',
    currency: apiJob.currency ?? '',
    applicationMethod: apiJob.applicationMethod ?? '',
    externalLink: apiJob.externalLink ?? null,
    applied: apiJob.applied ?? false,
    confidential: isConfidential,
  };
}

type SimilarJobApi = {
  id: string;
  positionTitle?: string;
  city?: string;
  country?: string;
  minSalary?: number | null;
  maxSalary?: number | null;
  currency?: string;
  workplace?: string;
  categories?: { id: string; name: string }[];
  department?: string;
  companyName?: string;
  companyLogoUrl?: string;
  jobType?: string;
  hideSalary?: boolean;
  confidential?: boolean;
};

function mapSimilarApiJob(apiJob: SimilarJobApi, index: number = 0): Job {
  const isConfidential = !!apiJob.confidential;
  return {
    id: apiJob.id,
    title: apiJob.positionTitle ?? 'Unknown Position',
    company: isConfidential ? 'Confidential' : (apiJob.companyName ?? 'Unknown Company'),
    companyId: '',
    companyLogoUrl: !isConfidential && apiJob.companyLogoUrl
      ? `${environment.apiImagePath}${apiJob.companyLogoUrl.replace(/\\/g, '/')}`
      : '',
    companyColor: getColor(index),
    companyInitial: isConfidential
      ? 'C'
      : (apiJob.companyName ?? 'C').charAt(0).toUpperCase(),
    type: apiJob.jobType ?? 'Full-time',
    salaryMin: apiJob.hideSalary ? null : apiJob.minSalary ?? null,
    salaryMax: apiJob.hideSalary ? null : apiJob.maxSalary ?? null,
    hideSalary: apiJob.hideSalary ?? false,
    location:
      apiJob.city && apiJob.country
        ? `${apiJob.city}, ${apiJob.country}`
        : (apiJob.city ?? apiJob.country ?? 'N/A'),
    workplace: apiJob.workplace ?? '',
    postedAgo: '',
    postedDaysAgo: 0,
    minExperience: null,
    maxExperience: null,
    description: '',
    skills: [],
    categories: (apiJob.categories ?? []).map((category) => category.name),
    requirements: [],
    benefits: [],
    responsibilities: [],
    isNew: false,
    jobLevel: '',
    department: apiJob.department ?? '',
    currency: apiJob.currency ?? '',
    applicationMethod: '',
    externalLink: null,
    applied: false,
    confidential: isConfidential,
  };
}

@Component({
  selector: 'app-jop-description',
  templateUrl: './jop-description.component.html',
  styleUrls: ['./jop-description.component.css'],
})
export class JopDescriptionComponent implements OnInit, OnDestroy {
  job: Job | undefined;
  isLoading = true;
  hasError = false;
  shareCopied = false;

  private rawApiJob: JobList | null = null;
  private shareTimeout?: ReturnType<typeof setTimeout>;

  extras: {
    description: SafeHtml;
    industry: string;
    tags: string[];
    skills: string[];
    categories: string[];
    responsibilities: SafeHtml[];
    requirements: SafeHtml[];
    benefits: SafeHtml[];
    postedDate: string;
  } | null = null;

  similarJobs: Job[] = [];
  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private jobListService: JobListService,
    private sanitizer: DomSanitizer,
  ) { }

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (!id) {
        this.router.navigate(['/job-list']);
        return;
      }

      this.job = undefined;
      this.extras = null;
      this.similarJobs = [];
      this.loadJob(id);
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    if (this.shareTimeout) clearTimeout(this.shareTimeout);
  }

  loadJob(id: string): void {
    this.isLoading = true;
    this.hasError = false;

    this.jobListService.getJobById(id).subscribe({
      next: (apiJob) => {
        this.rawApiJob = apiJob;

        this.job = mapApiJob(apiJob);
        this.similarJobs = (apiJob.similarJobs ?? []).map((similarJob, index) =>
          mapSimilarApiJob(similarJob as SimilarJobApi, index),
        );

        const skills = (this.job.skills ?? []).filter(Boolean);
        const categories = (this.job.categories ?? []).filter(Boolean);

        this.extras = {
          description: this.transformQuillHtml(this.job.description),
          industry: 'Technology & Internet',
          tags: categories,
          skills,
          categories,
          responsibilities: (this.job.responsibilities ?? []).map((r) =>
            this.transformResponsibilitiesHtml(r),
          ),
          requirements: (this.job.requirements ?? []).map((r) =>
            this.transformRequirementsHtml(r),
          ),
          benefits: (this.job.benefits ?? []).map((b) =>
            this.transformBenefitsHtml(b),
          ),
          postedDate: this.job.postedAgo,
        };
        this.isLoading = false;
      },
      error: () => {
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  async shareJob(): Promise<void> {
    const url = typeof window !== 'undefined' ? window.location.href : '';
    const title = this.job?.title ?? '';
    const company = this.job?.company ?? '';
    const text = `${title} at ${company}`;

    // Try native Web Share API first (works great on mobile)
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url });
        return;
      } catch {
        // User cancelled or share failed — fall through to clipboard
      }
    }

    // Fallback: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(url);
      this.shareCopied = true;
      if (this.shareTimeout) clearTimeout(this.shareTimeout);
      this.shareTimeout = setTimeout(() => {
        this.shareCopied = false;
      }, 2500);
    } catch {
      // Last resort: execCommand for very old browsers
      const el = document.createElement('textarea');
      el.value = url;
      el.style.position = 'fixed';
      el.style.opacity = '0';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.shareCopied = true;
      if (this.shareTimeout) clearTimeout(this.shareTimeout);
      this.shareTimeout = setTimeout(() => {
        this.shareCopied = false;
      }, 2500);
    }
  }

  goToApply(id: string): void {
    if (this.job?.applied) {
      return;
    }

    const externalLink = this.job?.externalLink?.trim();
    const isExternalLink =
      (this.job?.applicationMethod ?? '').toUpperCase() === 'EXTERNAL_LINK' &&
      !!externalLink;

    if (typeof window !== 'undefined' && isExternalLink && externalLink) {
      window.open(externalLink, '_blank', 'noopener,noreferrer');
      return;
    }

    this.router.navigate(['/apply-job', id], {
      state: {
        jobPostId: id,
        jobTitle: this.job?.title ?? '',
        companyName: this.job?.company ?? '',
        companyLogo: this.job?.confidential ? '' : (this.job?.companyLogoUrl ?? ''),
        companyInitials: this.job?.companyInitial ?? '',
        companyId: this.job?.confidential ? '' : (this.job?.companyId ?? ''),
        questions: this.rawApiJob?.questions ?? [],
      },
    });
  }

  getTagTheme(value: string): TagTheme {
    return TAG_THEMES[hashString(value) % TAG_THEMES.length];
  }

  transformBenefitsHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const items: string[] = [];
    const lis = doc.querySelectorAll('li');
    if (lis.length > 0) {
      lis.forEach((li) => {
        li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
        const text = li.textContent?.trim() ?? '';
        if (text) {
          items.push(`
            <div style="
              display:flex;
              align-items:center;
              gap:10px;
              background:#f8faff;
              border:1px solid #e8edf8;
              border-radius:8px;
              padding:12px 14px;
              font-size:13px;
              font-weight:500;
              color:#374151;
            ">
              <span style="
                display:flex;
                align-items:center;
                justify-content:center;
                width:28px;
                height:28px;
                min-width:28px;
                background:#e8f0ff;
                border-radius:6px;
                flex-shrink:0;
              ">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <circle cx="7" cy="7" r="6" stroke="#3575e2" stroke-width="1.2"/>
                  <path d="M4.5 7l2 2 3-3" stroke="#3575e2" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              ${text}
            </div>
          `);
        }
      });
    } else {
      const text = doc.body.textContent?.trim() ?? '';
      if (text) {
        items.push(`
          <div style="
            display:flex;
            align-items:center;
            gap:10px;
            background:#f8faff;
            border:1px solid #e8edf8;
            border-radius:8px;
            padding:12px 14px;
            font-size:13px;
            font-weight:500;
            color:#374151;
          ">
            <span style="
              display:flex;
              align-items:center;
              justify-content:center;
              width:28px;
              height:28px;
              min-width:28px;
              background:#e8f0ff;
              border-radius:6px;
              flex-shrink:0;
            ">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <circle cx="7" cy="7" r="6" stroke="#3575e2" stroke-width="1.2"/>
                <path d="M4.5 7l2 2 3-3" stroke="#3575e2" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
            ${text}
          </div>
        `);
      }
    }

    const grid = `<div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;">${items.join('')}</div>`;
    return this.sanitizer.bypassSecurityTrustHtml(grid);
  }

  transformRequirementsHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const items: string[] = [];
    const lis = doc.querySelectorAll('li');
    if (lis.length > 0) {
      lis.forEach((li) => {
        li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
        const text = li.textContent?.trim() ?? '';
        if (text) {
          items.push(`
            <div style="
              display:flex;
              align-items:flex-start;
              gap:8px;
              margin-bottom:10px;
              color:#424754;
              font-size:14px;
              line-height:1.6;
            ">
              <span style="color:#3575e2;font-size:18px;font-weight:700;line-height:1.2;flex-shrink:0;">›</span>
              ${text}
            </div>
          `);
        }
      });
    } else {
      const text = doc.body.textContent?.trim() ?? '';
      if (text) {
        items.push(`
          <div style="
            display:flex;
            align-items:flex-start;
            gap:8px;
            margin-bottom:10px;
            color:#424754;
            font-size:14px;
            line-height:1.6;
          ">
            <span style="color:#3575e2;font-size:18px;font-weight:700;line-height:1.2;flex-shrink:0;">›</span>
            ${text}
          </div>
        `);
      }
    }

    return this.sanitizer.bypassSecurityTrustHtml(items.join(''));
  }

  transformResponsibilitiesHtml(html: string): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const items: string[] = [];
    const lis = doc.querySelectorAll('li');
    if (lis.length > 0) {
      lis.forEach((li) => {
        li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
        const text = li.textContent?.trim() ?? '';
        if (text) {
          items.push(`
            <div style="
              display:flex;
              align-items:flex-start;
              gap:10px;
              margin-bottom:12px;
              color:#424754;
              font-size:14px;
              line-height:1.6;
            ">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="flex-shrink:0;margin-top:1px;">
                <circle cx="10" cy="10" r="9" stroke="#3575e2" stroke-width="1.4"/>
                <path d="M6.5 10l2.5 2.5 4.5-4.5" stroke="#3575e2" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              ${text}
            </div>
          `);
        }
      });
    } else {
      const text = doc.body.textContent?.trim() ?? '';
      if (text) {
        items.push(`
          <div style="
            display:flex;
            align-items:flex-start;
            gap:10px;
            margin-bottom:12px;
            color:#424754;
            font-size:14px;
            line-height:1.6;
          ">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" style="flex-shrink:0;margin-top:1px;">
              <circle cx="10" cy="10" r="9" stroke="#3575e2" stroke-width="1.4"/>
              <path d="M6.5 10l2.5 2.5 4.5-4.5" stroke="#3575e2" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            ${text}
          </div>
        `);
      }
    }

    return this.sanitizer.bypassSecurityTrustHtml(items.join(''));
  }

  transformQuillHtml(
    html: string,
    section: 'default' | 'benefits' | 'responsibilities' = 'default',
  ): SafeHtml {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    doc.querySelectorAll('li[data-list]').forEach((li) => {
      li.removeAttribute('data-list');
      li.querySelectorAll('.ql-ui').forEach((el) => el.remove());
    });

    doc.querySelectorAll('ol').forEach((ol) => {
      const ul = doc.createElement('ul');
      ul.innerHTML = ol.innerHTML;
      ol.replaceWith(ul);
    });

    doc.querySelectorAll('p').forEach((p) => {
      if (p.innerHTML === '<br>' || p.innerHTML === '') p.remove();
    });

    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  getExperienceLabel(): string {
    if (!this.job) return '';
    const { minExperience, maxExperience } = this.job;
    if (minExperience !== null && maxExperience !== null)
      return `${minExperience}–${maxExperience} years experience`;
    if (minExperience !== null) return `${minExperience}+ years experience`;
    if (maxExperience !== null) return `Up to ${maxExperience} years`;
    return '';
  }

  getWorkplaceLabel(workplace: string): string {
    const map: Record<string, string> = {
      REMOTE: 'Remote',
      ON_SITE: 'On-site',
      ONSITE: 'On-site',
      HYBRID: 'Hybrid',
    };
    return map[workplace] ?? workplace;
  }

  getSalaryDisplay(): string {
    if (!this.job || this.job.hideSalary) return 'Salary hidden';
    const min = this.job.salaryMin;
    const max = this.job.salaryMax;
    if (min !== null && max !== null) return `$${min}k – $${max}k`;
    if (min !== null) return `From $${min}k`;
    return '';
  }

  getSalaryFull(): string {
    if (!this.job || this.job.hideSalary) return 'Not disclosed';
    const min = this.job.salaryMin;
    const max = this.job.salaryMax;
    if (min !== null && max !== null) return `$${min},000 – $${max},000`;
    if (min !== null) return `From $${min},000`;
    return '';
  }

  getSimilarSalaryLabel(job: Job): string {
    if (job.hideSalary) return 'Salary hidden';
    const min = job.salaryMin;
    const max = job.salaryMax;
    const currency = job.currency || 'EGP';

    if (min !== null && max !== null) return `${currency} ${min} – ${currency} ${max}`;
    if (min !== null) return `From ${currency} ${min}`;
    if (max !== null) return `Up to ${currency} ${max}`;
    return 'Salary not disclosed';
  }

  getSimilarTags(j: Job): string[] {
    const tags = j.categories?.length ? j.categories : j.skills ?? [];
    return tags.slice(0, 2);
  }

  goBack(): void {
    this.router.navigate(['/job-list']);
  }

  goToJob(id: string): void {
    this.router.navigate(['/job-description', id]);
  }
}